import React, { createContext, useState, useContext, useEffect, ReactNode } from "react";
import { getAwards, updateAward } from "@/services/AwardService";
import { useAuth0 } from "@auth0/auth0-react";
import { useUser } from "@/context/UserContext";
import moment from "moment-timezone";
import useModalState from "@/hooks/useModalState";
import awards from "@/components/constants/awards.constants";
import { nthNumber, getStartAndEndOfWeek } from "@/helpers/helpers";
import { useNavigate, useLocation } from "react-router-dom";
import { cstMoment } from "@common/utils/date.utils";

interface Award {
    id: string;
    trophy_name: string;
    created_at: string;
    isViewed?: boolean;
}

interface AwardsData {
    awards: Award[];
    isLoading: boolean;
    isError: boolean;
}

interface AwardsContextType {
    data: AwardsData;
    refetch: () => void;
    handleNextAward: () => void;
}

interface GetHeadlineProps {
    isDaily: boolean;
    placement: string;
}

const AwardsContext = createContext<AwardsContextType | undefined>(undefined);

const INIT_AWARDS_VALUES: AwardsData = { awards: [], isLoading: true, isError: false };

/**
 * Checks if day today is Monday
 */
const isMonday = cstMoment().weekday() === 1;

interface AwardsProviderProps {
    children: ReactNode;
}

export const AwardsProvider = ({ children }: AwardsProviderProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated } = useAuth0();
    const { userState } = useUser();
    const [data, setData] = useState<AwardsData>(INIT_AWARDS_VALUES);
    const { setModalId, setModalContent } = useModalState();

    // Check if user is on Classic home page
    const isClassicHomePage = location.pathname === "/classic" || location.pathname === "/daily-properties";

    /**
     * Generates a congratulatory headline based on the provided parameters.
     *
     * @param options.isDaily - Indicates whether the achievement is for the top daily score.
     * @param options.placement - The placement or rank achieved.
     *
     * @returns A congratulatory headline.
     */
    const getHeadline = ({ isDaily, placement }: GetHeadlineProps): string => {
        if (isDaily) {
            return "Nice! You won a medal for having one of the top scores of the day!";
        } else {
            return `Amazing! You won ${placement} place trophy!`;
        }
    };

    /**
     * Handles award modal next button
     */
    const handleNextAward = (): void => {
        setData((prev) => ({
            ...prev,
            awards: prev.awards.filter((award) => award.id !== data.awards[0].id),
        }));

        const awardsCount = data.awards?.length || 0;

        if (awardsCount <= 1) {
            setModalId("");
            navigate("/classic");
        }
    };

    /**
     * Handles the display of awards information in a modal.
     */
    const handleShowAwards = React.useCallback(async () => {
        /**
         * Formats a date object into a text representation.
         *
         * @param date - The date to be formatted.
         * @returns A string representing the formatted date in the "MMM DD, YYYY" format.
         */
        const textDateFormat = (date: string): string => moment(date).format("MMM DD, YYYY");

        // Guard clause skip exec if isLoading, or no length
        if (data.isLoading || !data.awards.length) return;

        // Check if first award is daily
        const isDaily = data.awards[0].trophy_name.includes("DAILY");

        // Get award val from awards constant
        const award = Object.entries(awards.AWARDS)?.find(
            (award) => award[0] === data.awards[0].trophy_name,
        )?.[1] as any;

        const placement = nthNumber(award?.placement || 1);
        const awardsCount = data.awards?.length || 0;

        // Update award isViewed value to true
        await updateAward({ awardId: data.awards[0].id, isViewed: true });

        // Open awards modal
        setModalId("AWARDS_MODAL");

        const weekData = getStartAndEndOfWeek(moment(data.awards[0].created_at).format("M/DD/YYYY"));
        const startOfWeek = (weekData as any).startOfWeek;

        // Set awards modal content
        setModalContent({
            title: getHeadline({ isDaily, placement }),
            message: `${placement} Place ${
                isDaily ? textDateFormat(data.awards[0].created_at) : `Week of ${textDateFormat(startOfWeek)}`
            }`,
            ctaTitle: awardsCount > 1 ? "Next award" : isDaily ? "Keep playing" : "Defend your title!",
            icon: award?.icon || "",
            cb: () => {},
        });
    }, [data.awards, data.isLoading, setModalContent, setModalId]);

    /**
     * Fetches awards data based on user authentication status.
     */
    const fetchAwards = React.useCallback(async () => {
        if (!isAuthenticated || !data.isLoading || !userState?.id) return;

        const type = isMonday ? "weekly" : "daily";

        const awardsResponse = await getAwards({
            type,
            userId: userState?.id || "",
            isViewed: false,
        } as any);

        if (awardsResponse && Array.isArray(awardsResponse)) {
            setData((prev) => ({
                ...prev,
                awards: awardsResponse,
                isLoading: false,
                isError: false,
            }));
        } else {
            setData((prev) => ({ ...prev, awards: [], isLoading: false, isError: true }));
        }
    }, [data.isLoading, isAuthenticated, userState?.id]);

    /**
     * Handles refetching of fetch awards func
     */
    const refetch = (): void => setData((prev) => ({ ...prev, isLoading: true }));

    /**
     * Fetch awards
     */
    useEffect(() => {
        void fetchAwards();
    }, [fetchAwards]);

    /**
     * Executes show awards func - only on Classic home page
     */
    useEffect(() => {
        // Only show award modals on Classic home page
        if (isClassicHomePage) {
            void handleShowAwards();
        }
    }, [handleShowAwards, isClassicHomePage]);

    return <AwardsContext.Provider value={{ data, refetch, handleNextAward }}>{children}</AwardsContext.Provider>;
};

export const useAwards = (): AwardsContextType => {
    const context = useContext(AwardsContext);
    if (context === undefined) {
        throw new Error("useAwards must be used within an AwardsProvider");
    }
    return context;
};
